import type { TFunction } from "i18next";
import { ADVISOR_DEFAULT_SLUG } from "./advisor.data";
import type { IAdvisorStarter } from "./types";
import {
  Banknote,
  CalendarClock,
  CalendarDays,
  Clock,
  Landmark,
  MessagesSquare,
  PlaneTakeoff,
  Receipt,
  Scale,
  ScrollText,
  Target,
  UsersRound,
  Wallet,
} from "lucide-react";

/**
 * Starter questions, keyed by advisor — the personal-scoped counterpart to
 * `company-starters.data.ts`. Shared by the My Space AI
 * Assistant page and the Maya sidebar. Same advisors, same structure, but
 * "my payslip" instead of "this month's payroll": suggestions that don't
 * match the selected advisor are worse than no suggestions, and suggestions
 * phrased for a company-wide audience are wrong here too.
 *
 * `prompt` is the fully-formed query sent to Maya; `title`/`description` are
 * what the user reads. All three are translated — `prompt` is also what gets
 * sent as the user's own chat message, so it should read in the UI language.
 */
const buildStarters = (t: TFunction): Record<string, IAdvisorStarter[]> => ({
  [ADVISOR_DEFAULT_SLUG]: [
    {
      icon: CalendarDays,
      title: t("ai-assistant:starters.my_space.default.my_leave_balance.title"),
      description: t(
        "ai-assistant:starters.my_space.default.my_leave_balance.description",
      ),
      prompt: t(
        "ai-assistant:starters.my_space.default.my_leave_balance.prompt",
      ),
    },
    {
      icon: Clock,
      title: t(
        "ai-assistant:starters.my_space.default.my_attendance_this_month.title",
      ),
      description: t(
        "ai-assistant:starters.my_space.default.my_attendance_this_month.description",
      ),
      prompt: t(
        "ai-assistant:starters.my_space.default.my_attendance_this_month.prompt",
      ),
    },
    {
      icon: Wallet,
      title: t(
        "ai-assistant:starters.my_space.default.my_latest_payslip.title",
      ),
      description: t(
        "ai-assistant:starters.my_space.default.my_latest_payslip.description",
      ),
      prompt: t(
        "ai-assistant:starters.my_space.default.my_latest_payslip.prompt",
      ),
    },
    {
      icon: PlaneTakeoff,
      title: t("ai-assistant:starters.my_space.default.apply_for_leave.title"),
      description: t(
        "ai-assistant:starters.my_space.default.apply_for_leave.description",
      ),
      prompt: t(
        "ai-assistant:starters.my_space.default.apply_for_leave.prompt",
      ),
    },
  ],
  "uae-labour-law": [
    {
      icon: Landmark,
      title: t(
        "ai-assistant:starters.my_space.uae_labour_law.my_end_of_service_estimate.title",
      ),
      description: t(
        "ai-assistant:starters.my_space.uae_labour_law.my_end_of_service_estimate.description",
      ),
      prompt: t(
        "ai-assistant:starters.my_space.uae_labour_law.my_end_of_service_estimate.prompt",
      ),
    },
    {
      icon: CalendarClock,
      title: t(
        "ai-assistant:starters.my_space.uae_labour_law.my_notice_period.title",
      ),
      description: t(
        "ai-assistant:starters.my_space.uae_labour_law.my_notice_period.description",
      ),
      prompt: t(
        "ai-assistant:starters.my_space.uae_labour_law.my_notice_period.prompt",
      ),
    },
    {
      icon: CalendarDays,
      title: t(
        "ai-assistant:starters.my_space.uae_labour_law.my_leave_entitlement.title",
      ),
      description: t(
        "ai-assistant:starters.my_space.uae_labour_law.my_leave_entitlement.description",
      ),
      prompt: t(
        "ai-assistant:starters.my_space.uae_labour_law.my_leave_entitlement.prompt",
      ),
    },
    {
      icon: Scale,
      title: t(
        "ai-assistant:starters.my_space.uae_labour_law.is_my_leave_policy_compliant.title",
      ),
      description: t(
        "ai-assistant:starters.my_space.uae_labour_law.is_my_leave_policy_compliant.description",
      ),
      prompt: t(
        "ai-assistant:starters.my_space.uae_labour_law.is_my_leave_policy_compliant.prompt",
      ),
    },
  ],
  "hr-advisor": [
    {
      icon: Target,
      title: t(
        "ai-assistant:starters.my_space.hr_advisor.prepare_for_my_review.title",
      ),
      description: t(
        "ai-assistant:starters.my_space.hr_advisor.prepare_for_my_review.description",
      ),
      prompt: t(
        "ai-assistant:starters.my_space.hr_advisor.prepare_for_my_review.prompt",
      ),
    },
    {
      icon: MessagesSquare,
      title: t(
        "ai-assistant:starters.my_space.hr_advisor.have_a_hard_conversation.title",
      ),
      description: t(
        "ai-assistant:starters.my_space.hr_advisor.have_a_hard_conversation.description",
      ),
      prompt: t(
        "ai-assistant:starters.my_space.hr_advisor.have_a_hard_conversation.prompt",
      ),
    },
    {
      icon: UsersRound,
      title: t(
        "ai-assistant:starters.my_space.hr_advisor.grow_in_my_role.title",
      ),
      description: t(
        "ai-assistant:starters.my_space.hr_advisor.grow_in_my_role.description",
      ),
      prompt: t(
        "ai-assistant:starters.my_space.hr_advisor.grow_in_my_role.prompt",
      ),
    },
    {
      icon: ScrollText,
      title: t(
        "ai-assistant:starters.my_space.hr_advisor.understand_a_policy.title",
      ),
      description: t(
        "ai-assistant:starters.my_space.hr_advisor.understand_a_policy.description",
      ),
      prompt: t(
        "ai-assistant:starters.my_space.hr_advisor.understand_a_policy.prompt",
      ),
    },
  ],
  "payroll-tax": [
    {
      icon: Landmark,
      title: t(
        "ai-assistant:starters.my_space.payroll_tax.understand_my_payslip.title",
      ),
      description: t(
        "ai-assistant:starters.my_space.payroll_tax.understand_my_payslip.description",
      ),
      prompt: t(
        "ai-assistant:starters.my_space.payroll_tax.understand_my_payslip.prompt",
      ),
    },
    {
      icon: Receipt,
      title: t(
        "ai-assistant:starters.my_space.payroll_tax.my_tax_obligations.title",
      ),
      description: t(
        "ai-assistant:starters.my_space.payroll_tax.my_tax_obligations.description",
      ),
      prompt: t(
        "ai-assistant:starters.my_space.payroll_tax.my_tax_obligations.prompt",
      ),
    },
    {
      icon: Banknote,
      title: t(
        "ai-assistant:starters.my_space.payroll_tax.my_pension_contributions.title",
      ),
      description: t(
        "ai-assistant:starters.my_space.payroll_tax.my_pension_contributions.description",
      ),
      prompt: t(
        "ai-assistant:starters.my_space.payroll_tax.my_pension_contributions.prompt",
      ),
    },
    {
      icon: Clock,
      title: t(
        "ai-assistant:starters.my_space.payroll_tax.when_will_i_get_paid.title",
      ),
      description: t(
        "ai-assistant:starters.my_space.payroll_tax.when_will_i_get_paid.description",
      ),
      prompt: t(
        "ai-assistant:starters.my_space.payroll_tax.when_will_i_get_paid.prompt",
      ),
    },
  ],
});

export const advisorStarters = (
  slug: string,
  t: TFunction,
): IAdvisorStarter[] => {
  const starters = buildStarters(t);
  return starters[slug] ?? starters[ADVISOR_DEFAULT_SLUG];
};
